<template>
  <v-card id="sort-inspector">
    <v-card-title class="primary white--text">
      <v-icon color="tertiary" class="mr-2">mdi-magnify-scan</v-icon>
      <span class="headline">{{ $t('sortInspector') }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text class="pt-4">
      <v-row align="center" dense>
        <v-col cols="12" md="auto">
          <div class="subtitle-1 font-weight-medium">{{ $t('productionBySort') }}</div>
          <div class="caption text--secondary">{{ $t('netMbfDeliveredPerSort') }}</div>
        </v-col>
        <v-spacer/>
        <v-col cols="12" md="4" lg="3" v-if="destinations.length > 0">
          <Selector
          dense
          itemText="label"
          :items="destinations"
          :label="$t('destination')"
          :initialItem="initialDestination"
          prependInnerIcon="mdi-domain"
          elementId="sort-inspector-destination-selector"
          @item-selected="destinationSelected"/>
        </v-col>
        <v-col cols="12" md="4" lg="3" v-if="destinations.length > 0">
          <Selector
          dense
          selectFirstOnChange
          itemText="tractTypeName"
          :items="tractTypes"
          :label="$t('tractType')"
          :equality="sameTractType"
          prependInnerIcon="mdi-target-variant"
          elementId="sort-inspector-tract-type-selector"
          @item-selected="tractType = $event"/>
        </v-col>
      </v-row>
      <v-data-table
      dense
      show-expand
      item-key="key"
      class="small-headers expandable-rows"
      :headers="headers"
      :items="sortGroups"
      :loading="loading"
      :expanded.sync="expanded"
      :items-per-page="-1"
      :no-data-text="noDataText"
      hide-default-footer
      @click:row="(item, row) => row.expand(!row.isExpanded)">
        <template #header.effectiveRate="{ header }">
          <v-row no-gutters align="center" justify="end">
            <v-col cols="auto">
              <span>{{ header.text }}</span>
            </v-col>
            <v-col cols="auto">
              <Icon
              class="ml-1"
              icon="mdi-information-outline"
              iconColor="info"
              :tooltipText="$t('effectiveRateHint')"/>
            </v-col>
          </v-row>
        </template>
        <template #item.share="{ item }">
          <div class="d-flex align-center">
            <v-progress-linear rounded height="6" color="primary" class="share-bar" :value="item.share"/>
            <span class="caption ml-2 share-percentage text-right">{{ formatPercentage(item.share) }}</span>
          </div>
        </template>
        <template #item.netVolume="{ item }">{{ mbfStringFromBf(item.netVolume) }}</template>
        <template #item.effectiveRate="{ item }">{{ effectiveRateStringFor(item) }}</template>
        <template #item.amount="{ item }">{{ formatMoney(item.amount) }}</template>
        <template #expanded-item="{ headers: expandedHeaders, item }">
          <td :colspan="expandedHeaders.length" class="pa-4">
            <SortContractCards
            :contracts="item.contracts"
            :destinationName="destinationName"
            @contract-selected="contractSelected"/>
          </td>
        </template>
        <template #body.append v-if="sortGroups.length > 0">
          <tr class="font-weight-bold">
            <td colspan="3">{{ $t('total') }}</td>
            <td class="text-right">{{ mbfStringFromBf(deliveredNetVolume) }}</td>
            <td class="text-right">{{ totalContractCount }}</td>
            <td class="text-right">{{ effectiveRateStringFor({ amount: totalAmount, netVolume: deliveredNetVolume }) }}</td>
            <td class="text-right">{{ formatMoney(totalAmount) }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
    <Dialog :stateId="scaleSummaryDialogId" maxWidth="2000px" @close="focusedContract = undefined">
      <ContractSortScaleSummary
      v-if="focusedContract !== undefined"
      :contract="focusedContract"
      :destinationName="destinationName"
      :accountName="focusedContractTicket?.account"
      :settingName="focusedContractTicket?.setting"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { uniqBy } from 'lodash'
import { sortKeyFor, sortLabelFor } from '@/utils/SortDimensions'
import { formatMoney, formatPercentage, mbfStringFromBf, effectiveRatePerMbf } from '@/utils/NumericMutations'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { TicketStatus } from '@/utils/Enumerations.js'

// The menu item is currently gated to the Logs ticket grid, whose contract types are Production and Woods Sale.
const LOGS_CONTRACT_TYPES = {
  includeProduction: true,
  includeWoodsSale: true,
  includeLogYardSale: false,
  includeTransfer: false
}

export default {
  name: 'SortInspector',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ContractSortScaleSummary: () => import('@/components/ticket/sort-inspector/ContractSortScaleSummary.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Selector: () => import('@/components/core/Selector.vue'),
    SortContractCards: () => import('@/components/ticket/sort-inspector/SortContractCards.vue')
  },

  data: () => ({
    rows: [],
    expanded: [],
    destination: undefined,
    tractType: undefined,
    storedDestinationAccountId: undefined,
    focusedContract: undefined,
    scaleSummaryDialogId: uniqueDialogId('contract-sort-scale-summary')
  }),

  computed: {
    ...mapGetters('ticket', ['allTickets']),
    ...mapGetters('sort-inspector', ['loading']),

    qualifyingTickets () {
      return this.allTickets.filter(ticket =>
        ticket.status === TicketStatus.ReadyForPayment.value &&
        ticket.netMbf > 0 &&
        ticket.destinationAccountId != null)
    },

    destinations () {
      const destinations = new Map()

      for (const ticket of this.qualifyingTickets) {
        if (ticket.containsBaseRate || destinations.has(ticket.destinationAccountId)) continue

        destinations.set(ticket.destinationAccountId, {
          destinationAccountId: ticket.destinationAccountId,
          name: ticket.destination,
          label: this.addNameWithCode(ticket.destination, ticket.destinationCode)
        })
      }

      return [...destinations.values()].sort((a, b) => a.label.localeCompare(b.label))
    },

    initialDestination () {
      return this.destinations.find(destination => destination.destinationAccountId === this.storedDestinationAccountId)
    },

    destinationName () {
      return this.destination?.name ?? this.$t('notAvailable')
    },

    tractTypes () {
      const tractTypes = uniqBy(this.rows.filter(row => row.tractTypeId != null), 'tractTypeId')
        .map(({ tractTypeId, tractTypeName }) => ({ tractTypeId, tractTypeName }))
        .sort((a, b) => a.tractTypeName.localeCompare(b.tractTypeName))

      return [{ tractTypeId: undefined, tractTypeName: this.$t('allTractTypes') }, ...tractTypes]
    },

    filteredRows () {
      return this.tractType?.tractTypeId === undefined
        ? this.rows
        : this.rows.filter(row => row.tractTypeId === this.tractType.tractTypeId)
    },

    focusedContractTicket () {
      return this.allTickets.find(ticket => ticket.contractId === this.focusedContract?.contractId)
    },

    noDataText () {
      if (this.destinations.length > 0) return this.$t('noSortDataForDestination')

      return this.qualifyingTickets.length > 0
        ? this.$t('allReadyForPaymentTicketsContainBaseRate')
        : this.$t('noDestinationsReadyForPayment')
    },

    headers () {
      return [
        { text: this.$t('sort'), value: 'label', width: '30%' },
        { text: this.$t('shareOfNetVolume'), value: 'share', width: '25%' },
        { text: this.$t('netMbf'), value: 'netVolume', align: 'right' },
        { text: this.$t('contracts'), value: 'contractCount', align: 'right' },
        { text: this.$t('effectiveRate'), value: 'effectiveRate', align: 'right', sortable: false },
        { text: this.$t('amount'), value: 'amount', align: 'right' }
      ]
    },

    deliveredNetVolume () {
      // Overlapping sorts repeat a ticket's delivered volume on every row rating it; count each ticket once.
      const ticketVolumes = new Map()
      for (const row of this.filteredRows) ticketVolumes.set(row.ticketId, row.ticketNetVolume)
      return [...ticketVolumes.values()].reduce((total, volume) => total + volume, 0)
    },

    sortGroups () {
      const groups = new Map()

      for (const row of this.filteredRows) {
        const key = sortKeyFor(row)

        if (!groups.has(key)) {
          groups.set(key, {
            key,
            label: sortLabelFor(row, this.$t('unsorted')),
            netVolume: 0,
            amount: 0,
            contractsById: new Map()
          })
        }

        const group = groups.get(key)
        group.netVolume += row.netVolume
        group.amount += row.amount

        const contract = group.contractsById.get(row.contractId)
        if (contract) {
          contract.netVolume += row.netVolume
          contract.grossVolume += row.grossVolume
          contract.amount += row.amount
          contract.ticketCount += 1
        } else {
          group.contractsById.set(row.contractId, {
            contractId: row.contractId,
            tractName: row.tractName,
            netVolume: row.netVolume,
            grossVolume: row.grossVolume,
            amount: row.amount,
            ticketCount: 1
          })
        }
      }

      return [...groups.values()]
        .map(({ contractsById, ...group }) => {
          const contracts = [...contractsById.values()].sort((a, b) => b.netVolume - a.netVolume)
          return { ...group, contracts, contractCount: contracts.length, share: this.shareFor(group) }
        })
        .sort((a, b) => b.netVolume - a.netVolume)
    },

    totalAmount () {
      return this.sortGroups.reduce((total, group) => total + group.amount, 0)
    },

    totalContractCount () {
      return new Set(this.filteredRows.map(row => row.contractId)).size
    }
  },

  created () {
    const stored = localStorage.getItem(LocalStorageKeys.SORT_INSPECTOR_DESTINATION_ID)
    const storedId = stored ? Number(JSON.parse(stored)) : undefined
    this.storedDestinationAccountId = Number.isFinite(storedId) ? storedId : undefined
  },

  methods: {
    ...mapActions('sort-inspector', ['fetchProductionBySort']),
    ...mapActions('dialog', ['openDialog']),
    formatMoney,
    formatPercentage,
    mbfStringFromBf,

    destinationSelected (destination) {
      this.destination = destination
      localStorage.setItem(LocalStorageKeys.SORT_INSPECTOR_DESTINATION_ID, JSON.stringify(destination.destinationAccountId))
      this.refresh()
    },

    async refresh () {
      if (this.destination === undefined) return
      this.expanded = []

      const production = await this.fetchProductionBySort({
        destinationAccountId: this.destination.destinationAccountId,
        contractTypes: LOGS_CONTRACT_TYPES
      })

      this.rows = production ?? []
    },

    contractSelected (contract) {
      this.focusedContract = contract
      this.openDialog({ id: this.scaleSummaryDialogId, width: '90vw', allowFullscreen: true })
    },

    sameTractType (a, b) {
      return a?.tractTypeId === b?.tractTypeId
    },

    effectiveRateStringFor ({ amount, netVolume }) {
      const rate = effectiveRatePerMbf(amount, netVolume)
      return rate === null ? this.$t('notAvailable') : formatMoney(rate)
    },

    shareFor ({ netVolume }) {
      if (!this.deliveredNetVolume) return 0
      return Math.round(netVolume / this.deliveredNetVolume * 10000) / 100
    }
  }
}
</script>

<style scoped>
.expandable-rows ::v-deep tbody > tr:not(.v-data-table__expanded__content):not(.font-weight-bold) {
  cursor: pointer;
}

.share-bar {
  flex: 1 1 0;
}

.share-percentage {
  flex: 0 0 56px;
}
</style>
